import { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { usersApi } from '../../services/api';
import { useSnackbar } from 'notistack';

export default function ChangePasswordForm() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{text: string; isError: boolean} | null>(null);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setMessage(null);
      setErrors({});
      
      // Call the actual backend API
      await usersApi.changePassword({
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword
      });
      
      // Success response
      setMessage({
        text: 'Password changed successfully!',
        isError: false
      });
      
      // Show success notification
      enqueueSnackbar('Password changed successfully!', { 
        variant: 'success',
        autoHideDuration: 5000
      });
      
      // Clear the form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      console.error('Error changing password:', error);
      
      // Handle specific error messages from backend
      let errorMessage = 'Failed to change password. Please try again.';
      
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Handle specific validation errors
      if (errorMessage.includes('Current password is incorrect')) {
        setErrors({ currentPassword: 'Current password is incorrect' });
        errorMessage = 'Current password is incorrect';
      } else if (errorMessage.includes('Password must be at least')) {
        setErrors({ newPassword: 'Password must be at least 6 characters long' });
      } else if (errorMessage.includes('Passwords do not match')) {
        setErrors({ confirmPassword: 'Passwords do not match' });
      }
      
      setMessage({
        text: errorMessage,
        isError: true
      });
      
      // Show error notification
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Change Password
      </Typography>
      
      {message && (
        <Typography 
          color={message.isError ? 'error' : 'success'}
          gutterBottom
          sx={{ mb: 2 }}
        >
          {message.text}
        </Typography>
      )}
      
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Current Password"
          name="currentPassword"
          type="password"
          value={formData.currentPassword}
          onChange={handleChange}
          error={!!errors.currentPassword}
          helperText={errors.currentPassword}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="New Password"
          name="newPassword"
          type="password"
          value={formData.newPassword}
          onChange={handleChange}
          error={!!errors.newPassword}
          helperText={errors.newPassword}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="Confirm New Password"
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          required
        />
        
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              'Change Password'
            )}
          </Button>
        </Box>
      </form>
    </Paper>
  );
}
